import { Box, Stack, Typography } from '@mui/material';
import { LinkedPoiRow } from './LinkedPoiRow';
import { useAtom, useAtomValue } from 'jotai';
import * as cuboidState from '../../../../../cuboids/state';
import * as poiState from '../../../../state';
import { AssemblyPoiWithCheckMark } from './types';
import { useCallback, useEffect } from 'react';
import { findPointsInsideCuboid, Number3 } from '@abyss/3d-viewer';
import * as state from '@/components/Analysis/state';
import { enumToString } from '@/utils/string';

export const LinkedPoiEditor = () => {
  const [cuboidLinkedPois, setCuboidLinkedPois] = useAtom(cuboidState.cuboidLinkedPois);
  const currentCuboid = useAtomValue(cuboidState.currentCuboid);
  const poiFilteredByCheckbox = useAtomValue(poiState.poiFilteredByCheckbox);
  const allVoiTemplates = useAtomValue(state.allVoiTemplates);
  const allPaintBlockVoiTemplates = useAtomValue(state.allPaintBlockVoiTemplates);

  // Makes sure whenever current cuboid changes, we recalculate the points inside the cuboid
  useEffect(() => {
    if (!currentCuboid) return;
    const poisExcludingVois = poiFilteredByCheckbox
      ?.filter((poi) => poi.centerPoint3d)
      .filter(
        (poi) =>
          !allVoiTemplates.some(
            (voiTemplate) => voiTemplate.toLowerCase() === enumToString(poi.type).toLowerCase()
          )
      )
      .filter(
        (poi) =>
          !allPaintBlockVoiTemplates.some(
            (voiTemplate) => voiTemplate.name.toLowerCase() === enumToString(poi.type).toLowerCase()
          )
      );
    const poiPositions: Number3[] = poisExcludingVois
      .map((poi) => {
        if (!poi.centerPoint3d) return undefined;
        const { x, y, z } = poi.centerPoint3d;
        const point: Number3 = [x, y, z];
        return point;
      })
      .filter((point) => !!point);

    const poisInsideCuboidIndexes = findPointsInsideCuboid(currentCuboid, poiPositions);

    const poiWithinCuboidWithCheckbox: AssemblyPoiWithCheckMark[] = poisInsideCuboidIndexes.map(
      (poiIndex) => ({
        ...poisExcludingVois[poiIndex],
        checked: true,
      })
    );
    setCuboidLinkedPois(poiWithinCuboidWithCheckbox);
  }, [
    allPaintBlockVoiTemplates,
    allVoiTemplates,
    currentCuboid,
    poiFilteredByCheckbox,
    setCuboidLinkedPois,
  ]);

  const onCheck = useCallback(
    (id: string) => {
      setCuboidLinkedPois((current) =>
        current?.map((poi) => {
          if (poi.id === id) {
            return {
              ...poi,
              checked: !poi.checked,
            };
          }
          return poi;
        })
      );
    },
    [setCuboidLinkedPois]
  );

  return (
    <Box>
      <Typography fontSize={'1.5rem'}>Linked POIs</Typography>
      {cuboidLinkedPois && cuboidLinkedPois.length > 0 ? (
        <Stack>
          {cuboidLinkedPois.map((poi) => (
            <LinkedPoiRow key={poi.id} poiInfo={poi} onCheck={onCheck} />
          ))}
        </Stack>
      ) : (
        <Typography>No linked POIs</Typography>
      )}
    </Box>
  );
};
